import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';

import { SideNavComponent } from '../Layout/side-nav/side-nav.component';
import { AuthService } from './modules/auth/auth.service';
import { AppDataService } from './core/services/app-data.service';
import { watchMobileTableLabels } from './core/utils/table-mobile.util';

const IDLE_PROMPT_MS = 10 * 60 * 1000;
const IDLE_LOGOUT_MS = 20 * 60 * 1000;
const CHECK_EVERY_MS = 15 * 1000;

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SideNavComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly appData = inject(AppDataService);
  private stopTableWatch: (() => void) | null = null;
  private sessionTimer: ReturnType<typeof setInterval> | null = null;
  private lastActivityAt = Date.now();
  private readonly onUserActivity = (): void => {
    if (this.showSessionWarning()) {
      return;
    }
    this.lastActivityAt = Date.now();
  };

  title = 'ims-frontend';
  sideNavOpen = false;
  showShell = false;

  protected readonly showSessionWarning = signal(false);

  ngOnInit() {
    this.updateShellVisibility(this.router.url);
    this.stopTableWatch = watchMobileTableLabels();
    this.bindActivityListeners();
    this.startSessionWatch();

    if (this.auth.isAuthenticated()) {
      this.appData.refreshAll();
      this.auth.refreshAccess();
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navigation = event as NavigationEnd;
        this.updateShellVisibility(navigation.urlAfterRedirects);
        this.sideNavOpen = false;
        this.auth.refreshAccess();
        this.checkSessionExpiry();
      });
  }

  ngOnDestroy() {
    this.stopTableWatch?.();
    this.unbindActivityListeners();
    if (this.sessionTimer) {
      clearInterval(this.sessionTimer);
      this.sessionTimer = null;
    }
  }

  toggleSideNav() {
    this.sideNavOpen = !this.sideNavOpen;
  }

  closeSideNav() {
    this.sideNavOpen = false;
  }

  extendSession(): void {
    this.lastActivityAt = Date.now();
    this.showSessionWarning.set(false);
  }

  endIdleSession(): void {
    this.showSessionWarning.set(false);
    this.auth.logout();
    void this.router.navigate(['/login'], {
      queryParams: { reason: 'idle' },
    });
  }

  private startSessionWatch(): void {
    this.checkSessionExpiry();
    this.sessionTimer = setInterval(() => this.checkSessionExpiry(), CHECK_EVERY_MS);
  }

  private checkSessionExpiry(): void {
    if (!this.showShell || !this.auth.isAuthenticated()) {
      this.showSessionWarning.set(false);
      this.lastActivityAt = Date.now();
      return;
    }

    const idleFor = Date.now() - this.lastActivityAt;
    if (idleFor >= IDLE_LOGOUT_MS) {
      this.endIdleSession();
      return;
    }

    if (idleFor >= IDLE_PROMPT_MS) {
      this.showSessionWarning.set(true);
    }

    const remaining = this.auth.msUntilExpiry();
    if (remaining != null && remaining <= 0) {
      this.showSessionWarning.set(false);
      this.auth.clearLocalSession();
      void this.router.navigate(['/login'], {
        queryParams: { reason: 'expired' },
      });
    }
  }

  private bindActivityListeners(): void {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const;
    for (const eventName of events) {
      document.addEventListener(eventName, this.onUserActivity, { passive: true });
    }
  }

  private unbindActivityListeners(): void {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const;
    for (const eventName of events) {
      document.removeEventListener(eventName, this.onUserActivity);
    }
  }

  private updateShellVisibility(url: string) {
    const path = url.split('?')[0] || '/';
    const authPaths = ['/', '/login', '/otp', '/forgot-password', '/reset-password'];
    const nextShell = !authPaths.includes(path);
    if (nextShell && !this.showShell) {
      this.lastActivityAt = Date.now();
      this.showSessionWarning.set(false);
    }
    this.showShell = nextShell;
  }
}
